import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getRoofType,
  RoofTypeModel,
  updateRoofType,
  UpdateRoofTypeRequest,
  deleteRoofType,
} from "../../clients/BackOfficeApi";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useIsAuthenticated } from "@azure/msal-react";
import {
  ValidationErrorModel,
  isApiError,
  isBadRequest,
} from "../../utils/apiUtils";
import { getDynamicMessageId } from "../../components/I18n";
import { useIntl } from "react-intl";
import { ErrorList } from "../../elements/ErrorList";
import { DetailPageHeader } from "../../compositions/DetailPageHeader";
import { ActionButton } from "../../elements/ActionButton";
import { DeleteIcon } from "@chakra-ui/icons";
import { useDirty } from "../../contexts/DirtyContext";

export function RoofTypeDetailPage() {
  const roofTypeId = Number(useParams().roofTypeId);
  const [validationError, setValidationError] =
    useState<ValidationErrorModel<UpdateRoofTypeRequest>>();
  const [error, setError] = useState<string>();
  const isAuthenticated = useIsAuthenticated();
  const intl = useIntl();
  const navigate = useNavigate();
  const [dirty, setDirty] = useDirty(true);

  const [loading, setLoading] = useState(true);

  const [roofType, setRoofType] = useState<RoofTypeModel>();
  const [modifiedRoofType, setModifiedRoofType] =
    useState<UpdateRoofTypeRequest>();

  const isDirty = useMemo(() => {
    if (!roofType || !modifiedRoofType) return false;

    return roofType.name !== modifiedRoofType.name;
  }, [roofType, modifiedRoofType]);

  useEffect(() => {
    setDirty(isDirty);
  }, [isDirty, setDirty]);

  const fetchRoofType = useCallback(
    async (signal?: AbortSignal) => {
      if (!isAuthenticated) return;

      setError(undefined);
      setLoading(true);

      if (!Number.isInteger(roofTypeId) || roofTypeId <= 0) {
        setError("Ongeldig daktype ID");
        setLoading(false);
        return;
      }

      getRoofType({
        path: { roofTypeId },
        signal,
        throwOnError: true,
      })
        .then(({ data }) => {
          setRoofType(data);
          setModifiedRoofType((c) => c ?? data);
        })
        .catch((e) => {
          if (signal?.aborted) return;

          setError(`Fout bij ophalen daktype: ${e.message ?? String(e)}`);
        })
        .finally(() => setLoading(false));
    },
    [isAuthenticated, roofTypeId],
  );

  useEffect(() => {
    const abortController = new AbortController();

    fetchRoofType(abortController.signal);

    return () => abortController.abort("Component unmounted or re-rendered");
  }, [fetchRoofType]);

  const update = useCallback(() => {
    if (!isAuthenticated || !roofType || !modifiedRoofType || !dirty) return;

    setError(undefined);
    setValidationError(undefined);

    updateRoofType({
      path: { roofTypeId },
      body: modifiedRoofType,
      throwOnError: true,
    })
      .catch((e) => {
        if (isApiError(e)) {
          const errorId = getDynamicMessageId(e.title ?? "Unknown error");

          setError(
            intl.formatMessage({ id: errorId, defaultMessage: e.title ?? "Unknown error" }),
          );

          if (isBadRequest<UpdateRoofTypeRequest>(e)) {
            setValidationError(e.errors);
          }
        } else {
          setError(`Fout bij bijwerken daktype: ${e}`);
        }
      })
      .finally(() => {
        fetchRoofType();
      });
  }, [
    isAuthenticated,
    roofType,
    roofTypeId,
    modifiedRoofType,
    dirty,
    fetchRoofType,
    intl,
  ]);

  const fetchDeleteRoofType = useCallback(
    (confirmed: boolean) => {
      if (!confirmed) return;

      deleteRoofType({
        path: { roofTypeId },
        throwOnError: true,
      }).then(() => navigate(".."));
    },
    [navigate, roofTypeId],
  );

  return (
    <Box>
      <DetailPageHeader>
        <ActionButton
          color="red.500"
          confirmDescription="Weet je zeker dat je dit daktype wilt verwijderen? Deze actie kan niet ongedaan gemaakt worden."
          confirmTitle="Verwijderen bevestigen"
          icon={<DeleteIcon />}
          isDestructive
          onClick={fetchDeleteRoofType}
          title="Verwijderen"
        />
      </DetailPageHeader>
      {loading && <Spinner />}
      {error && <Text color="red.500">{error}</Text>}
      {roofType && (
        <Box>
          <Heading>{roofType.name}</Heading>
          {modifiedRoofType && (
            <>
              <FormControl isInvalid={!!validationError?.Name} mb={2}>
                <FormLabel htmlFor="roofType.name">Naam</FormLabel>
                <Input
                  id="roofType.name"
                  isInvalid={!!validationError?.Name}
                  name="roofType.name"
                  onChange={(e) =>
                    setModifiedRoofType({
                      ...modifiedRoofType,
                      name: e.target.value,
                    })
                  }
                  value={modifiedRoofType.name}
                />
                <ErrorList errors={validationError?.Name} />
              </FormControl>
              <Button isDisabled={!dirty} mt={2} onClick={update}>
                Opslaan
              </Button>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
